import React, { useMemo } from 'react';

import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { getTextContent } from '../../../common/elements/utils/elementPropertyUtils';
import { createInitialSingleEditorTaskListDocument } from '../../../common/taskLists/singleEditorTaskListContentBuilder';
import { isSingleEditorTaskList } from '../../../common/taskLists/taskListContentVersionUtils';
import { TiptapContent } from '../../../common/tiptap/tiptapTypes';
import { SaveTiptapEditorContentFunction } from '../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import { asObject } from '../../../common/utils/immutableHelper';
import TaskListEditor from './editor/TaskListEditor';
import LegacyTaskListEditor, { LegacyTaskListEditorProps } from './LegacyTaskListEditor';

type TaskListEditorSwitchProps = Omit<
    LegacyTaskListEditorProps,
    'currentEditorId' | 'isEditing' | 'saveContent' | 'spellCheck' | 'startEditing'
> & {
    element: ImMNElement;
    textContent?: TiptapContent | null;
    saveContent: SaveTiptapEditorContentFunction;
    isEditing: boolean;
    currentEditorId?: string;
    startEditing: (args: { editorId: string; editorKey: string }) => void;
    spellCheck: boolean;
};

type SingleTaskListEditorProps = {
    element: ImMNElement;
    saveContent: SaveTiptapEditorContentFunction;
    isEditable: boolean;
    isEditing: boolean;
    isSingleSelected: boolean;
    isFocusedForegroundElement?: boolean;
    currentEditorId?: string;
    startEditing: (args: { editorId: string; editorKey: string }) => void;
    spellCheck: boolean;
    permissions?: number;
    moveToTrashHandler?: (args: { elementId: string }) => unknown;
};

/**
 * Renders the single-editor task list with memoised conversion from persisted immutable content.
 */
const SingleTaskListEditor: React.FC<SingleTaskListEditorProps> = ({
    element,
    saveContent,
    isEditable,
    isEditing,
    isSingleSelected,
    isFocusedForegroundElement,
    currentEditorId,
    startEditing,
    spellCheck,
    permissions,
    moveToTrashHandler,
}) => {
    const taskListTextContent = getTextContent(element);
    const textContent = useMemo(
        () =>
            taskListTextContent
                ? asObject<TiptapContent>(taskListTextContent)
                : createInitialSingleEditorTaskListDocument(),
        [taskListTextContent],
    );

    return (
        <TaskListEditor
            element={element}
            textContent={textContent}
            saveContent={saveContent}
            isEditable={isEditable}
            isEditing={isEditing}
            isSingleSelected={isSingleSelected}
            isFocusedForegroundElement={isFocusedForegroundElement}
            currentEditorId={currentEditorId}
            startEditing={startEditing}
            spellCheck={spellCheck}
            permissions={permissions}
            moveToTrashHandler={moveToTrashHandler}
        />
    );
};

/**
 * Switches the task list body between the legacy nested-task renderer and the single-editor renderer.
 */
const TaskListEditorSwitch: React.FC<TaskListEditorSwitchProps> = (props) => {
    const {
        element,
        saveContent,
        isEditable,
        isEditing,
        isSingleSelected,
        isFocusedForegroundElement,
        currentEditorId,
        startEditing,
        spellCheck,
        permissions,
        moveToTrashHandler,
    } = props;

    if (isSingleEditorTaskList(element))
        return (
            <SingleTaskListEditor
                element={element}
                saveContent={saveContent}
                isEditable={isEditable}
                isEditing={isEditing}
                isSingleSelected={isSingleSelected}
                isFocusedForegroundElement={isFocusedForegroundElement}
                currentEditorId={currentEditorId}
                startEditing={startEditing}
                spellCheck={spellCheck}
                permissions={permissions}
                moveToTrashHandler={moveToTrashHandler}
            />
        );

    return <LegacyTaskListEditor {...props} />;
};

export default TaskListEditorSwitch;
